import React, { useEffect, useRef, useState } from "react";

// Reveal hook for animation
const useContainerReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.classList.add("reveal-in");
        io.disconnect();
      }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

const WhatsAppAdsPage = () => {
  const contentRef = useRef(null);
  const [response, setResponse] = useState("");
  const [waOn, setWaOn] = useState(false); // WhatsApp icon toggle
  const [typing, setTyping] = useState(false); // State for typing animation

  const showResponse = (msg) => {
    setTyping(true);
    setTimeout(() => {
      setResponse(msg);
      setTyping(false);
      setTimeout(() => setResponse(""), 4000); // Longer display for realism
    }, 1000); // Simulate typing delay
  };

  const toggleWA = () => {
    setWaOn((v) => !v);
    const media = contentRef.current.querySelector(".wa-media");
    if (media) {
      media.classList.add("bounce");
      setTimeout(() => media.classList.remove("bounce"), 500);
      showResponse(
        waOn
          ? "Chat closed. Reopen to continue! 🚪"
          : "Hello! How can I assist you today? 👋"
      ); // Dynamic response
    }
  };

  const handleKeyToggle = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleWA();
    }
  };

  return (
    <div
      className="whatsapp-ads-container"
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: "#ffffff",
        padding: "60px",
        maxWidth: "1450px",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      {/* Header Row */}
      <div
        ref={contentRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch", // full-height columns
          gap: "60px",
          transition: "all 0.6s ease-out",
        }}
        className="reveal-in"
      >
        {/* Left Text Column */}
        <div style={{ flex: 1, minWidth: "350px", display: "flex", flexDirection: "column" }}>
          <h1
            style={{
              fontSize: "3.6rem",
              fontWeight: "700",
              fontFamily: "inter",
              marginBottom: "20px",
              color: "#222",
              transition: "all 0.6s ease-out",
            }}
            className="fade-in-up"
          >
            Grow Faster with <span style={{ color: "#0078D4" }}>WhatsApp Ads</span>
          </h1>

          <h2
            style={{
              fontSize: "1.1rem",
              marginBottom: "30px",
              color: "#555",
               fontFamily: "Poppins",
                fontWeight: 300,
              transition: "all 0.7s ease-out 0.2s",
              lineHeight: 1.2,
            }}
            className="fade-in-up"
          >
            Turn clicks into real conversations. Launch click-to-WhatsApp campaigns, automate
            replies, and convert leads in minutes.
          </h2>

          <div style={{ display: "flex", gap: "20px" }}>
            <button
              onClick={() => showResponse("🚀 You clicked Get Started!")}
              className="wa-btn wa-btn-green"
            >
              Get Started
            </button>
            <button
              onClick={() => showResponse("💰 You clicked See Pricing!")}
              className="wa-btn wa-btn-green"
            >
              See Pricing
            </button>
          </div>

          {response && (
            <div className={`wa-response ${typing ? "typing" : ""}`} style={{ marginTop: "20px" }}>
              {typing ? (
                <span className="typing-dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              ) : (
                response
              )}
            </div>
          )}
        </div>

        {/* Right Feature Card (clickable media toggles WhatsApp icon) */}
        <div
          style={{
            flex: 1,
            minWidth: "350px",
            backgroundColor: "#e6f3e6", // Light green background
            padding: "45px",
            borderRadius: "25px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.7s ease-out, transform 0.7s ease-out 0.3s",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
          className="fade-in-up"
        >
          {/* CLICKABLE media area */}
          <div
            className="wa-media"
            role="button"
            tabIndex={0}
            title="Click to toggle WhatsApp icon"
            onClick={toggleWA}
            onKeyDown={handleKeyToggle}
            style={{
              height: "220px",
              borderRadius: "12px",
              marginBottom: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              userSelect: "none",
              background: waOn
                ? "linear-gradient(135deg, #e6f3e6, #d9f7d9)" // Deeper green when ON
                : "linear-gradient(135deg, #e6f3e6, #f1f9f1)", // Light green when OFF
              boxShadow: waOn ? "inset 0 0 0 2px #1DB95433" : "inset 0 0 0 1px #00000010",
              transition: "background 220ms ease, box-shadow 220ms ease, transform 120ms ease",
              position: "relative",
            }}
          >
            {waOn ? (
              // === WhatsApp icon (green circle with WA monogram) ===
              <div className="wa-icon-badge" aria-label="WhatsApp message icon">
                <div className="wa-circle">
                  <span className="wa-mono">WA</span>
                </div>
              </div>
            ) : (
              // default chat bubble
              <div className="wa-bubble">💬</div>
            )}
            <span className="wa-wave" aria-hidden="true" />
          </div>

          {/* Stats pinned at bottom */}
          <div style={{ display: "flex", justifyContent: "space-around", gap: "15px", marginTop: "auto" }}>
            <div style={{ textAlign: "center", flex: 1, minWidth: "120px" }}>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#0078D4" }}>90 sec</div>
              <div style={{ fontSize: "1.1rem", color: "#555" }}>Avg. response</div>
            </div>
            <div style={{ textAlign: "center", flex: 1, minWidth: "120px" }}>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#0078D4" }}>-40%</div>
              <div style={{ fontSize: "1.1rem", color: "#555" }}>CPL reduction</div>
            </div>
            <div style={{ textAlign: "center", flex: 1, minWidth: "120px" }}>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#0078D4" }}>3Bn+</div>
              <div style={{ fontSize: "1.1rem", color: "#555" }}>Reachable users</div>
            </div>
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style>{`
        .fade-in-up { opacity: 1 !important; transform: translateY(0) !important; }
        .whatsapp-ads-container { max-width: 1450px; margin: 0 auto; padding: 60px; background: #fff; font-family: 'Inter', sans-serif; overflow: hidden; }

        /* Buttons + response box */
        .wa-btn { padding: 20px 35px; border-radius: 8px; font-weight: 700; cursor: pointer; font-size: 1.2rem; transition: all 0.2s ease, transform 0.3s ease; position: relative; overflow: hidden; }
        .wa-btn:active { transform: scale(0.95); }
        .wa-btn-green { background: #1DB954; color: #fff; border: none; box-shadow: 0 4px 15px rgba(29, 185, 84, 0.25); }
        .wa-btn-green:hover { background: #169c43; box-shadow: 0 6px 20px rgba(29, 185, 84, 0.35); transform: scale(1.05) translateY(-2px); }
        .wa-btn-green:focus { outline: none; }
        .wa-btn-green::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }
        .wa-btn-green:active::after {
          width: 200px;
          height: 200px;
        }
        .wa-response { margin-top: 20px; padding: 12px 20px; background: #e6f3fa; border: 1px solid #0078D4; border-radius: 8px; color: #0078D4; font-weight: 500; animation: fadeIn 0.3s ease; }
        .wa-response.typing { display: flex; align-items: center; }
        .typing-dots span { font-size: 1.2rem; margin: 0 2px; animation: typingBlink 1s infinite; }
        .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes typingBlink { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }

        /* Media content inside the right card */
        .wa-bubble { font-size: 2.5rem; color: #1DB954; transform: scale(1); transition: transform 120ms ease, color 0.3s ease; }
        .wa-media:active .wa-bubble { transform: scale(0.95); }
        .wa-media.bounce { animation: bounceEffect 0.5s ease-out; }
        .wa-media:hover .wa-wave { animation: waveEffect 1s ease-out infinite; }
        .wa-media:hover .wa-bubble { color: #169c43; } /* Darker green on hover */
        @keyframes bounceEffect { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1) translateY(-5px); } }

        /* Wave effect on hover */
        .wa-wave {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(29, 185, 84, 0.1) 0%, transparent 70%);
          border-radius: 12px;
          opacity: 0;
          pointer-events: none;
        }
        @keyframes waveEffect { 0% { transform: scale(0); opacity: 0.5; } 50% { transform: scale(1.5); opacity: 0.2; } 100% { transform: scale(2); opacity: 0; } }

        /* WhatsApp icon badge */
        .wa-icon-badge { display: flex; align-items: center; justify-content: center; animation: pop 0.18s ease; }
        .wa-circle {
          width: 96px;
          height: 96px;
          border-radius: 999px;
          background: #1DB954; /* Changed to green */
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(29, 185, 84, 0.35), inset 0 -4px 10px rgba(0, 0, 0, 0.08);
          animation: pulse 1.5s infinite ease-in-out;
          transition: transform 0.3s ease;
        }
        .wa-icon-badge:hover .wa-circle { transform: scale(1.1); }
        .wa-mono {
          font-weight: 900;
          color: #ffffff;
          font-size: 34px;
          letter-spacing: 0.5px;
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          transition: color 0.3s ease;
        }
        .wa-icon-badge:hover .wa-mono { color: #ffffff; } /* Ensure color remains white on hover */
        @keyframes pop { from { transform: scale(0.85); opacity: 0.7; } to { transform: scale(1); opacity: 1; } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }

        @media (max-width: 900px) { .whatsapp-ads-container { padding: 40px 20px; } }
        @media (max-width: 560px) { .whatsapp-ads-container { padding: 30px 15px; } }
        @media (prefers-reduced-motion: reduce) {
          .wa-btn, .wa-icon-badge, .wa-media, .wa-response { transition: none !important; animation: none !important; }
          .wa-wave { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppAdsPage;